import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/cn';

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

// Radix's TooltipContent does NOT portal itself by default — only
// TooltipPrimitive.Portal does that. Without it, content renders inline at
// the trigger's position in the DOM, so any ancestor with `overflow-hidden`/
// `overflow-auto` (kanban lane columns, scrollable drawers, cards with
// `overflow-hidden` for rounded corners, etc.) clips it, and ancestors that
// create a stacking context (CSS `transform`, used by the kanban's
// hover/drag lift and by react-beautiful-dnd while dragging) can place it
// beneath sibling cards even with a high z-index. Portaling to <body/> and
// keeping a z-index above every other overlay (dialogs/sheets/popovers all
// use z-50) fixes both: no ancestor overflow/stacking-context can clip or
// bury it, anywhere in the app.
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[60] overflow-hidden rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background shadow-soft-md',
        'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95',
        'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
